import {
	addDays,
	isValid,
	parseISO,
	formatISO,
	subYears,
	subDays,
	format,
	formatDistanceToNow
} from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";

const padNumber = (n) => {
	const padN = "00" + n;
	return padN.substring(padN.length - 2);
};

export const getFormattedTime = (t) => t.toISOString();

export const getUTCDatetime = (dt) => new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours(),
	dt.getMinutes(), dt.getSeconds(), dt.getMilliseconds()));

export const getUTCDisplayTime = (t, errorMsg, s) => {
	const isZeroTime = t === "0001-01-01T00:00:00Z";
	const separator = s ? s : "-";

	if (t && !isZeroTime) {
		const date = new Date(t);
		return date.getUTCFullYear() + separator +
			padNumber(date.getUTCMonth() + 1) + separator +
			padNumber(date.getUTCDate()) + " " +
			padNumber(date.getUTCHours()) + ":" +
			padNumber(date.getUTCMinutes());
	}

	if (!errorMsg) {
		return "Unavailable";
	}

	return errorMsg;
};

export function formatDateOnHourRFC3339(dateText, hour, timezone) {
	var date = new Date(dateText);
	const day = date.getDate();
	const month = date.getMonth() + 1;
	const year = date.getFullYear();
	const naive = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T${String(hour).padStart(2, "0")}:00:00`;
	return zonedTimeToUtc(naive, timezone).toISOString();
}

export function formatDateInZone(date, locale, timeZone) {
	const day = new Intl.DateTimeFormat(locale, { day: "numeric", timeZone }).format(date);
	const month = new Intl.DateTimeFormat(locale, { month: "short", timeZone }).format(date);
	const year = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone }).format(date);

	// Extract short time-zone code (e.g. BST or GMT)
	const tz = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
		.format(date)
		.split(" ")
		.pop();

	return `${day} ${month} ${year} (${tz})`;
}

export function formatDateTimeInZone(date, locale, timeZone) {
	const day = new Intl.DateTimeFormat(locale, { day: "numeric", timeZone }).format(date);
	const month = new Intl.DateTimeFormat(locale, { month: "short", timeZone }).format(date);
	const year = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone }).format(date);
	const time = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone }).format(date);

	// Extract short time-zone code (e.g. BST or GMT)
	const tz = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: "short" })
		.format(date)
		.split(" ")
		.pop();

	return `${day} ${month} ${year} at ${time} (${tz})`;
}

export const getDisplayTime = (t, errorMsg, s) => {
	const isZeroTime = t === "0001-01-01T00:00:00Z";
	const separator = s ? s : "-";

	if (t && !isZeroTime) {
		const date = new Date(t);
		return date.getFullYear() + separator +
			padNumber(date.getMonth() + 1) + separator +
			padNumber(date.getDate()) + " " +
			padNumber(date.getHours()) + ":" +
			padNumber(date.getMinutes());
	}

	if (!errorMsg) {
		return "Unavailable";
	}

	return errorMsg;
};

export const getUTCDisplayDate = (t, errorMsg, s) => {
	const isZeroTime = t === "0001-01-01T00:00:00Z";
	const separator = s ? s : "-";

	if (t && !isZeroTime) {
		const date = new Date(t);
		return date.getUTCFullYear() + separator +
			padNumber(date.getUTCMonth() + 1) + separator +
			padNumber(date.getUTCDate());
	}

	if (!errorMsg) {
		return "Unavailable";
	}

	return errorMsg;
};

export const getDisplayDate = (t, errorMsg, s) => {
	const isZeroTime = t === "0001-01-01T00:00:00Z";
	const separator = s ? s : "-";

	if (t && !isZeroTime) {
		const date = new Date(t);
		return date.getFullYear() + separator +
			padNumber(date.getMonth() + 1) + separator +
			padNumber(date.getDate());
	}

	if (!errorMsg) {
		return "Unavailable";
	}

	return errorMsg;
};

export const getDisplayDateTime = (t, errorMsg, s) => {
	const displayDate = getDisplayDate(t, errorMsg, s);

	if (typeof displayDate === "string" && displayDate !== "Unavailable") {
		const date = new Date(t);
		const time = `${padNumber(date.getHours())}:${padNumber(date.getMinutes())}:${padNumber(date.getSeconds())}`;
		return `${displayDate} ${time}`;
	}

	return displayDate;
};

export const getCurrentDateTruncated = () => {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	return date;
};

export const getNextDayTruncated = () => {
	let tomorrow = new Date();
	tomorrow.setDate(tomorrow.getDate() + 1);
	tomorrow.setHours(1);
	tomorrow.setMinutes(0);
	tomorrow.setSeconds(0);
	tomorrow.setMilliseconds(0);
	return tomorrow;
};

export const getNextAvailableDeliveryDay = (date, isUserAdmin) => {

	let newDate;

	if (isUserAdmin) {
		newDate = new Date();
	} else {
		newDate = new Date(date);
		let day = newDate.getDay(); // get the current day of the week (0 is Sunday, 1 is Monday, etc.)
		let daysToAdd;

		switch (day) {
		case 4: // Thursday
			daysToAdd = 4;
			break;
		case 5: // Friday
			daysToAdd = 4;
			break;
		case 6: // Saturday
			daysToAdd = 3;
			break;
		default:
			daysToAdd = 2;
		}

		newDate = addDays(newDate, daysToAdd);
	}

	return newDate;
};

export const isISODate = (d) => {
	return typeof d === "string" && isValid(parseISO(d));
};

export const isValidDate = (d) => d instanceof Date && isValid(d);

export const convertToISO = (d) => {
	if (isISODate(d)) {
		return d;
	} else if (!(d instanceof Date)) {
		return ("Input must be a Date object or a valid ISO 8601 date string.");
	}

	const formattedDateString = formatISO(d).substring(0, 19);
	return formattedDateString;
};

export const getISODateTime = (d, t) => {
	if (!d || !t || !isISODate(d)) return;

	let date = d;
	date = date.substring(0, 10);
	let time = "T" + t;
	return date + time;
};

export const nullDate = "0001-01-01T00:00:00Z";

export const isDateNull = (date) => date === nullDate;

const normaliseDate = (dateString) => {
	const date = new Date(dateString);
	date.setHours(0, 0, 0, 0); // Set time to midnight (00:00:00.000)
	return date;
};

export const isDateValidAndMatch = (expectedDate, actualDate) => {
	if (isDateNull(actualDate) || isDateNull(expectedDate)) return false;

	const normalisedActualDate = normaliseDate(actualDate);
	const normalisedExpectedDate = normaliseDate(expectedDate);

	return normalisedActualDate <= normalisedExpectedDate;
};

export function getDateMinusOneYearPlusOneDay() {
	const today = new Date();
	today.setHours(0, 0, 0, 0);
	const minusOneYear = subYears(today, 1);
	const minusOneYearPlusOneDay = addDays(minusOneYear, 1);
	return minusOneYearPlusOneDay;
}

// Orders page default window (BEZ-5787). Shared so initial load and Filter reset stay aligned.
export const ORDERS_DEFAULT_DATE_RANGE_DAYS = 30;

// Widen action for Orders zero-results recovery (BEZ-5790).
export const ORDERS_WIDEN_DATE_RANGE_DAYS = 365;

export function getDateMinusDays(days) {
	const date = getCurrentDateTruncated();
	date.setDate(date.getDate() - days);
	return date;
}

const areTruncatedDatesEqual = (a, b) => (
	isValidDate(a) && isValidDate(b) && a.toISOString() === b.toISOString()
);

export function isOrdersDefaultDateRange(startDate, endDate) {
	return areTruncatedDatesEqual(endDate, getCurrentDateTruncated())
		&& areTruncatedDatesEqual(startDate, getDateMinusDays(ORDERS_DEFAULT_DATE_RANGE_DAYS));
}

export function isOrdersWidenDateRange(startDate, endDate) {
	return areTruncatedDatesEqual(endDate, getCurrentDateTruncated())
		&& areTruncatedDatesEqual(startDate, getDateMinusDays(ORDERS_WIDEN_DATE_RANGE_DAYS));
}

/**
 * Label for Orders date constraints (chips, results copy, search placeholder, empty state).
 * Rolling presets only when end is today — custom ranges of similar length show explicit dates.
 */
export function getOrdersDateRangeLabel(startDate, endDate) {
	if (!isValidDate(startDate) || !isValidDate(endDate)) {
		return "";
	}

	const today = getCurrentDateTruncated();
	if (areTruncatedDatesEqual(endDate, today)) {
		if (areTruncatedDatesEqual(startDate, getDateMinusDays(ORDERS_DEFAULT_DATE_RANGE_DAYS))) {
			return "Last 30 days";
		}
		if (areTruncatedDatesEqual(startDate, today)) {
			return "Today";
		}
		if (areTruncatedDatesEqual(startDate, getDateMinusDays(ORDERS_WIDEN_DATE_RANGE_DAYS))) {
			return "Last 12 months";
		}
	}

	return `${format(startDate, "d MMM yyyy")} – ${format(endDate, "d MMM yyyy")}`;
}

export const getLast30DaysDates = () => {
	const today = new Date();
	const startDate = subDays(today, 30);

	return {
		startDate: format(startDate, "yyyy-MM-dd'T'00:00:00"),
		endDate: format(today, "yyyy-MM-dd'T'00:00:00"),
	};
};

export function formatLastUpdatedDate(date) {
	return formatDistanceToNow(date, {
		addSuffix: true
	});
}
